import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ComparisonWizard from './ComparisonWizard';
import { getComparisons, compareModels, parseComparisonStream, getComparisonDetail, deleteComparison } from '../../services/modelComparisonApi';
import { getModelDisplayName } from '../../constants/models';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useModal } from '../../contexts/ModalContext';
import { highlightMarkdown } from '../common/MarkdownHighlighter';
import ComparisonResults from './ComparisonResults';

const CompareModelsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { comparisonId } = useParams();
  const { currentWorkspace } = useWorkspace();
  const { showConfirm } = useModal();
  
  // Check if we're on the /compare/new route
  const isNewRoute = location.pathname === '/compare/new';
  const [viewMode, setViewMode] = useState(isNewRoute ? 'wizard' : 'allComparisons'); // 'allComparisons', 'wizard', 'streaming', 'view'
  const [comparisons, setComparisons] = useState([]);
  const [comparisonsLoading, setComparisonsLoading] = useState(true);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter comparisons based on search query
  const filteredComparisons = useMemo(() => {
    if (!searchQuery) return comparisons;
    const searchLower = searchQuery.toLowerCase();
    return comparisons.filter(comp => {
      const compName = comp.comparison_name || '';
      return compName.toLowerCase().includes(searchLower);
    });
  }, [comparisons, searchQuery]);
  
  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [output1, setOutput1] = useState('');
  const [output2, setOutput2] = useState('');
  const [streamingConfig, setStreamingConfig] = useState(null);
  const [results, setResults] = useState(null);
  const output1Ref = useRef(null);
  const output2Ref = useRef(null);

  const loadAllComparisons = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setComparisonsLoading(true);
    try {
      const workspaceId = currentWorkspace.id || currentWorkspace._id;
      const compData = await getComparisons({ workspaceId, limit: 100 });
      
      if (compData.comparisons) {
        setComparisons(compData.comparisons);
      }
    } catch (err) {
      console.error('Failed to load comparisons:', err);
      toast.error('Failed to load comparisons');
    } finally {
      setComparisonsLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    loadAllComparisons();
  }, [currentWorkspace, loadAllComparisons]);

  // Handle route-based view mode
  useEffect(() => {
    if (location.pathname === '/compare/new') {
      setViewMode('wizard');
    } else if (location.pathname === '/compare' && viewMode === 'wizard') {
      setViewMode('allComparisons');
    }
  }, [location.pathname, viewMode]);

  useEffect(() => {
    if (isStreaming) {
      if (output1Ref.current) {
        output1Ref.current.scrollTop = output1Ref.current.scrollHeight;
      }
      if (output2Ref.current) {
        output2Ref.current.scrollTop = output2Ref.current.scrollHeight;
      }
    }
  }, [output1, output2, isStreaming]);

  // Handle URL param for direct comparison view
  useEffect(() => {
    const viewComparisonFromUrl = async () => {
      if (comparisonId && comparisons.length > 0) {
        const comp = comparisons.find(c => c.id === comparisonId);
        if (comp) {
          try {
            const detail = await getComparisonDetail(comp.id);
            setSelectedComparison(detail);
            setViewMode('view');
          } catch (err) {
            console.error('Failed to load comparison details:', err);
            toast.error('Failed to load comparison details');
          }
        }
      }
    };
    viewComparisonFromUrl();
  }, [comparisonId, comparisons]);

  const handleNewComparison = () => {
    setViewMode('wizard');
    navigate('/compare/new');
  };

  const handleWizardComplete = async (config) => {
    setStreamingConfig(config);
    setViewMode('streaming');
    setIsStreaming(true);
    setOutput1('');
    setOutput2('');
    setResults(null);
    
    try {
      const workspaceId = currentWorkspace?.id || currentWorkspace?._id;
      const reader = await compareModels(
        config.project.id,
        config.comparisonName,
        config.inputValues,
        config.config1,
        config.config2,
        workspaceId
      );

      await parseComparisonStream(reader, {
        onStatus: (data) => {},
        onChunk: (data) => {
          if (data.model === 1) {
            setOutput1(prev => prev + data.content);
          } else if (data.model === 2) {
            setOutput2(prev => prev + data.content);
          }
        },
        onModelComplete: (data) => {},
        onComplete: async (data) => {
          // First set the streaming results
          setResults(data);
          
          // If we have a comparison ID, fetch the full details with accurate costs
          const comparisonId = data.comparison_id || data.id;
          if (comparisonId) {
            try {
              // Wait a moment for backend to finish saving
              await new Promise(resolve => setTimeout(resolve, 500));
              const fullDetails = await getComparisonDetail(comparisonId);
              setResults(fullDetails);
              setSelectedComparison(fullDetails);
            } catch (err) {
              console.error('Failed to fetch full comparison details:', err);
              // Keep the streaming results if fetch fails
            }
          }
          
          setIsStreaming(false);
          toast.success('Comparison completed successfully!');
          loadAllComparisons();
          
          // Auto-navigate to comparison details
          if (comparisonId) {
            setViewMode('view');
            navigate(`/compare-details/${comparisonId}`);
          }
        },
        onError: (data) => {
          toast.error(data.message || 'Comparison failed');
          setIsStreaming(false);
        }
      });
    } catch (err) {
      console.error('Comparison error:', err);
      toast.error(err.message);
      setIsStreaming(false);
    }
  };

  const handleWizardCancel = () => {
    setViewMode('allComparisons');
    navigate('/compare');
  };

  const handleBackToList = () => {
    setViewMode('allComparisons');
    setStreamingConfig(null);
    setSelectedComparison(null);
    setOutput1('');
    setOutput2('');
    setResults(null);
    navigate('/compare');
  };

  const handleViewComparison = async (comparison) => {
    try {
      const detail = await getComparisonDetail(comparison.id);
      setSelectedComparison(detail);
      setViewMode('view');
      navigate(`/compare-details/${comparison.id}`);
    } catch (err) {
      console.error('Failed to load comparison details:', err);
      toast.error('Failed to load comparison details');
    }
  };

  const handleDeleteComparison = async (comparisonId, e) => {
    e.stopPropagation();
    
    const confirmed = await showConfirm({
      title: 'Delete Comparison',
      message: 'Are you sure you want to delete this comparison? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteComparison(comparisonId);
      toast.success('Comparison deleted successfully');
      setComparisons(prev => prev.filter(c => c.id !== comparisonId));
      
      if (selectedComparison?.id === comparisonId) {
        handleBackToList();
      }
    } catch (err) {
      console.error('Failed to delete comparison:', err);
      toast.error('Failed to delete comparison');
    }
  };

  // Wizard View
  if (viewMode === 'wizard') {
    return (
      <ComparisonWizard
        onComplete={handleWizardComplete}
        onCancel={handleWizardCancel}
      />
    );
  }

  // Streaming View
  if (viewMode === 'streaming') {
    return (
      <div className="h-full flex flex-col bg-transparent overflow-hidden">
        <div className="page-header">
          <div className="page-header-content">
            <button
              onClick={handleBackToList}
              disabled={isStreaming}
              className="p-2 hover:bg-bg-hover rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed mr-3"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h2 className="page-title">{streamingConfig?.comparisonName}</h2>
              <p className="page-subtitle">{streamingConfig?.project?.project_name}</p>
            </div>
          </div>
          
          {isStreaming && (
            <div className="flex items-center gap-2 text-sm text-text-tertiary">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Running comparison...</span>
            </div>
          )}
        </div>

        {isStreaming && (
          <div className="prompts-view p-6">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-0 h-full">
              <div className="output-panel">
                <div className="output-tabs-container">
                  <div className="output-tabs">
                    <span className="output-tab active">{getModelDisplayName(streamingConfig?.config1?.model)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-text-tertiary">Generating...</span>
                  </div>
                </div>
                <pre ref={output1Ref} className="panel-content md-highlighted">
                  {output1 ? highlightMarkdown(output1) : 'Waiting for response...'}
                  {output1 && '▌'}
                </pre>
              </div>

              {/* Divider */}
              <div className="w-px bg-white/10 mx-4"></div>

              <div className="output-panel">
                <div className="output-tabs-container">
                  <div className="output-tabs">
                    <span className="output-tab active">{getModelDisplayName(streamingConfig?.config2?.model)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-text-tertiary">Generating...</span>
                  </div>
                </div>
                <pre ref={output2Ref} className="panel-content md-highlighted">
                  {output2 ? highlightMarkdown(output2) : 'Waiting for response...'}
                  {output2 && '▌'}
                </pre>
              </div>
            </div>
          </div>
        )}

        {!isStreaming && results && (
          <div className="flex-1 overflow-y-auto p-6">
            <ComparisonResults results={results} />
          </div>
        )}
      </div>
    );
  }

  // View Comparison Detail
  if (viewMode === 'view' && selectedComparison) {
    return (
      <div className="h-full flex flex-col bg-transparent overflow-hidden">
        <div className="pd-header">
          <button onClick={handleBackToList} className="btn-back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Comparisons
          </button>
          
          <div className="pd-header-content">
            <div className="pd-title-row">
              <h2 className="pd-title">{selectedComparison.comparison_name}</h2>
            </div>
            <div className="pd-metadata">
              {selectedComparison.created_at && (
                <span className="pd-meta-item">
                  <span className="pd-meta-label">Created:</span>
                  <span className="pd-meta-value">{new Date(selectedComparison.created_at).toLocaleString()}</span>
                </span>
              )}
              {selectedComparison.commit_id && (
                <span className="pd-meta-item">
                  <span className="pd-meta-label">Version:</span>
                  <code className="pd-commit-id">{selectedComparison.commit_id}</code>
                </span>
              )}
            </div>
          </div>
        </div>

        <ComparisonResults results={selectedComparison} />
      </div>
    );
  }

  // All Comparisons List View
  return (
    <div className="h-full flex flex-col bg-transparent overflow-hidden">
      <div className="page-header">
        <div className="page-header-content">
          <h2 className="page-title">Compare Models</h2>
          <p className="page-subtitle">
            {filteredComparisons.length}{searchQuery && ` of ${comparisons.length}`} {filteredComparisons.length === 1 ? 'comparison' : 'comparisons'}
          </p>
        </div>
        <div className="page-actions">
          <button onClick={handleNewComparison} className="btn-primary">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New Comparison
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-4 py-3 border-b border-border-primary/50">
        <div className="relative w-full">
          <svg 
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by comparison name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border-primary rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {comparisonsLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10 text-center">
          <div className="w-10 h-10 border-2 border-border-primary border-t-primary-500 rounded-full animate-spin"></div>
          <p className="text-text-secondary text-base mt-4">Loading comparisons...</p>
        </div>
      ) : filteredComparisons.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[500px] text-center p-16 max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-full bg-bg-tertiary flex items-center justify-center mb-6 border-2 border-border-primary">
            <svg className="w-10 h-10 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-text-primary text-2xl font-semibold m-0 mb-3">
            {searchQuery ? 'No Matching Comparisons' : 'No Comparisons Yet'}
          </h3>
          <p className="text-text-secondary text-base mb-6 leading-relaxed max-w-md">
            {searchQuery 
              ? 'No comparisons match your search query. Try adjusting your search.'
              : 'Start comparing AI models to find the best one for your use case. Run the same prompt on different models and see the results side-by-side.'
            }
          </p>
          <button 
            onClick={searchQuery ? () => setSearchQuery('') : handleNewComparison} 
            className="px-6 py-3 text-base font-semibold bg-primary-600 text-white rounded-lg cursor-pointer transition-all duration-fast inline-flex items-center gap-2 hover:bg-primary-500 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
          >
            {searchQuery ? (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12"/>
                </svg>
                Clear Search
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Create Your First Comparison
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-auto bg-bg-secondary">
          <table className="w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-bg-tertiary">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Name</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Project</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Model 1</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Model 2</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Created</th>
                <th className="py-3 px-4 text-center text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredComparisons.map((comp) => (
                <tr 
                  key={comp.id}
                  className="cursor-pointer transition-all duration-fast bg-bg-secondary hover:bg-bg-hover"
                  onClick={() => handleViewComparison(comp)}
                >
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle">
                    <div className="flex items-center gap-2">
                      <svg className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      <span className="font-semibold text-text-primary font-mono">{comp.comparison_name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle">
                    <span className="inline-block py-1 px-2 text-xs font-medium font-mono rounded-md whitespace-nowrap bg-bg-tertiary text-text-secondary border border-border-primary">{comp.project_name}</span>
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle">
                    <span className="font-mono text-sm">{getModelDisplayName(comp.model_1)}</span>
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle">
                    <span className="font-mono text-sm">{getModelDisplayName(comp.model_2)}</span>
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-tertiary align-middle w-44 text-[13px] whitespace-nowrap">
                    {new Date(comp.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary align-middle">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => handleDeleteComparison(comp.id, e)}
                        className="p-2 text-error-500 hover:bg-error-50 rounded-md transition-all duration-fast"
                        title="Delete comparison"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CompareModelsPage;
